import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Segment,
  Header,
  Grid,
  Button,
  Table,
  Loader,
  Message,
} from "semantic-ui-react";
import { DayPickerSingleDateController } from "react-dates";
import moment from "moment";
import "react-dates/lib/css/_datepicker.css";
import * as calendarUtils from "../../utils/calendarUtils";
import * as privateNewApi from "../../api/privateNewApi";
import Timeline from "./Timeline";

const Step2 = ({
  onNext,
  onPrevious,
  startDate,
  setStartDate,
  timeSlots,
  selectedTimeRange,
  setSelectedTimeRange,
  availableDates,
  isLoadingTimeSlots,
  setIsLoadingTimeSlots,
  isLoadingAvailableDates,
  onDateSelect,
  onTimeRangeConfirm,
  selectedDates,
  setSelectedDates,
  selectedSpecialita,
  selectedLivello,
  selectedArea,
  unavailableDates,
  startOrario,
  endOrario,
}) => {
  const { t } = useTranslation();
  const [tempTimeRange, setTempTimeRange] = useState(null);
  const [dataSelezionata, setDataSelezionata] = useState(null);
  const [focused, setFocused] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [calendarFocused, setCalendarFocused] = useState(false);

  // Get availability status for a time slot
  const getSlotAvailability = (currentSlot) => {
    const hour = parseInt(currentSlot.start.split(":")[0]);

    // Before 8 AM or after 6 PM - disabled
    if (hour < 8 || hour >= 18) {
      return "disabled";
    }

    // Check if it's in available time ranges
    const isAvailable = timeSlots.some(
      (timeSlot) =>
        currentSlot.start >= timeSlot.start && currentSlot.start < timeSlot.end
    );

    return isAvailable ? "available" : "blocked";
  };

  // Handle date selection from calendar
  const handleCalendarDateChange = (date) => {
    if (date) {
      const formattedDate = date.format("YYYY-MM-DD");
      setStartDate(formattedDate);
      setSelectedTimeRange(null);
      setTempTimeRange(null);
      // Add the start date to selected dates if not already present
      const isAlreadySelected = selectedDates.some((selectedDate) =>
        calendarUtils.isSameDay(selectedDate, date)
      );

      if (!isAlreadySelected) {
        setSelectedDates(
          [...selectedDates, date].sort((a, b) => {
            if (a.isBefore(b)) return -1;
            return 1;
          })
        );
      }

      fetchAvailabilityForDay(formattedDate);
    }
  };

  useEffect(() => {
    if (tempTimeRange == null || tempTimeRange === undefined) return;
    if (tempTimeRange.start == null || tempTimeRange.end == null) {
      setSelectedTimeRange(null);
    }
  }, [tempTimeRange]);

  // Handle date selection
  // const handleDateChange = (e) => {
  //   const selectedDate = e.target.value;
  //   setStartDate(selectedDate);
  //   setSelectedTimeRange(null);
  //   setTempTimeRange(null);
  //   if (selectedDate) {
  //     fetchAvailabilityForDay(selectedDate);
  //   }
  // };

  // Fetch availability data from API
  const fetchAvailabilityForDay = async (date) => {
    setIsLoadingTimeSlots(true);
    setApiError(null);

    try {
      // Format date to YYYY-MM-DD for API
      const formattedDate = moment(date).format("YYYY-MM-DD");

      // Call the API with parameters from Step 1
      const response = await privateNewApi.getDisponibilitàGiorno(
        formattedDate,
        selectedArea,
        selectedSpecialita,
        selectedLivello
      );

      // Transform API response to timeSlots format
      if (response && Array.isArray(response)) {
        const formattedTimeSlots = response.map((slot) => ({
          start: slot.dalle.slice(0, 5), // Convert "HH:mm:ss" to "HH:mm"
          end: slot.alle.slice(0, 5), // Convert "HH:mm:ss" to "HH:mm"
        }));

        // Use the onDateSelect callback to update the parent component state
        onDateSelect(formattedTimeSlots);
      } else {
        // Handle empty response
        onDateSelect([]);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
      setApiError("Impossibile caricare la disponibilità per questa data");
      onDateSelect([]);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Handle time slot selection
  const handleTimeSlotClick = (slot) => {
    const availability = getSlotAvailability(slot);
    if (availability !== "available") return;

    if (!tempTimeRange) {
      setTempTimeRange({ start: slot.start, end: null });
    } else if (!tempTimeRange.end) {
      if (slot.start > tempTimeRange.start) {
        // Calculate the actual end time (slot.start + 30 minutes)
        const endTime = new Date(`2000-01-01T${slot.start}:00`);
        endTime.setMinutes(endTime.getMinutes() + 30);
        const endTimeStr = endTime.toTimeString().slice(0, 5);

        // Check if selection is at least 1 hour
        const startTime = new Date(`2000-01-01T${tempTimeRange.start}:00`);
        const duration = (endTime - startTime) / (1000 * 60); // duration in minutes

        if (duration < 60) {
          // Less than 1 hour, don't allow selection
          return;
        }

        // Check if all slots between start and end are available
        if (isTimeRangeValid(tempTimeRange.start, endTimeStr)) {
          setTempTimeRange({ ...tempTimeRange, end: endTimeStr });
        } else {
          setTempTimeRange({ start: slot.start, end: null });
        }
      } else {
        setTempTimeRange({ start: slot.start, end: null });
      }
    } else {
      setTempTimeRange({ start: slot.start, end: null });
    }
  };

  // Check if a time range is valid (all slots are available)
  const isTimeRangeValid = (start, end) => {
    const startTime = new Date(`2000-01-01T${start}:00`);
    const endTime = new Date(`2000-01-01T${end}:00`);

    let current = new Date(startTime);
    while (current < endTime) {
      const currentTimeStr = current.toTimeString().slice(0, 5);
      const isSlotAvailable = timeSlots.some(
        (slot) => currentTimeStr >= slot.start && currentTimeStr < slot.end
      );
      if (!isSlotAvailable) {
        return false;
      }
      current.setMinutes(current.getMinutes() + 30);
    }
    return true;
  };

  // Confirm time range selection
  const handleTimeRangeConfirm = () => {
    if (tempTimeRange && tempTimeRange.start && tempTimeRange.end) {
      setSelectedTimeRange(tempTimeRange);
      onTimeRangeConfirm(tempTimeRange);

      setSelectedDates([moment(startDate)]);
    }
  };

  // Get selection instruction text
  const getSelectionInstructionText = () => {
    if (!tempTimeRange) {
      return (
        t("seleziona_ora_inizio") || "Seleziona l'ora di inizio (minimo 1 ora)"
      );
    } else if (!tempTimeRange.end) {
      return (
        t("seleziona_ora_fine") ||
        "Seleziona l'ora di fine (minimo 1 ora dalla partenza)"
      );
    } else {
      return (
        t("range_completato") ||
        "Range completato - conferma o seleziona nuovo orario"
      );
    }
  };

  // Handle calendar date selection
  const handleCalendarDateSelect = (date) => {
    setDataSelezionata(date);

    // Check if the date is already selected, toggle selection
    const dayIndex = selectedDates.findIndex((day) =>
      calendarUtils.isSameDay(day, date)
    );

    if (dayIndex > -1) {
      // Remove date if already selected
      setSelectedDates(
        selectedDates.filter((day) => !calendarUtils.isSameDay(day, date))
      );
    } else {
      // Add date if not already selected
      setSelectedDates(
        [...selectedDates, date].sort((a, b) => {
          if (a.isBefore(b)) return -1;
          return 1;
        })
      );
    }
  };

  // Check if a date is outside the available range
  const isOutsideRange = (day) => {
    // Return true if date is before start date
    if (startDate && day.isBefore(moment(startDate))) {
      return true;
    }

    // Return true if date is not in the available dates array
    if (availableDates && availableDates.length > 0) {
      const isAvailable = availableDates.some(
        (dateStr) =>
          moment(dateStr).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
      );
      return !isAvailable;
    }

    return false;
  };

  // Check if a date is outside the available range for start date selection
  const isOutsideRangeStartDate = (day) => {
    // Return true if date is before today
    if (day.isBefore(moment(), "day")) {
      return true;
    }

    // Return true if date is in the unavailable dates array
    if (unavailableDates && unavailableDates.length > 0) {
      const isUnavailable = unavailableDates.some(
        (dateStr) =>
          moment(dateStr).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
      );
      if (isUnavailable) return true;
    }

    // Return true if date is not in the available dates array
    if (availableDates && availableDates.length > 0) {
      const isAvailable = availableDates.some(
        (dateStr) =>
          moment(dateStr).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
      );
      return !isAvailable;
    }

    return false;
  };

  const canProceed =
    startDate && selectedTimeRange && availableDates.length > 0;

  return (
    <Segment>
      <Header as="h3">{t("date_e_orari") || "Date e orari"}</Header>

      <Grid divided>
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4">
              {t("seleziona_data_inizio") || "Seleziona data di inizio"}
            </Header>
            <Form.Field style={{ display: "flex", justifyContent: "center" }}>
              <DayPickerSingleDateController
                date={startDate ? moment(startDate) : null}
                onDateChange={handleCalendarDateChange}
                focused={calendarFocused}
                onFocusChange={({ focused }) => setCalendarFocused(focused)}
                isOutsideRange={isOutsideRangeStartDate}
                numberOfMonths={2}
                hideKeyboardShortcutsPanel={true}
                noBorder={true}
              />
            </Form.Field>
          </Grid.Column>
        </Grid.Row>

        {startDate && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as="h4">
                {t("seleziona_orario") || "Seleziona orario"}
              </Header>

              {apiError && (
                <Message negative>
                  <Message.Header>Errore</Message.Header>
                  <p>{apiError}</p>
                </Message>
              )}

              {isLoadingTimeSlots ? (
                <Loader active inline="centered" />
              ) : timeSlots.length > 0 ? (
                <>
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>
                          {t("orari_disponibili") || "Orari disponibili"}
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>
                          <Timeline
                            timeSlots={timeSlots}
                            tempTimeRange={tempTimeRange}
                            onTimeSlotClick={handleTimeSlotClick}
                            getSelectionInstructionText={
                              getSelectionInstructionText
                            }
                            //showConfirmButton={showConfirmButton}
                            onTimeRangeConfirm={onTimeRangeConfirm}
                            minHour={startOrario}
                            maxHour={endOrario}
                          />
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table>

                  {tempTimeRange &&
                    tempTimeRange.start &&
                    tempTimeRange.end &&
                    selectedTimeRange == null && (
                      <div style={{ marginTop: "10px" }}>
                        <Message info style={{ textAlign: "center" }}>
                          <Message.Header>
                            {t("range_selezionato") || "Range selezionato"}
                          </Message.Header>
                          <p>
                            {tempTimeRange.start} - {tempTimeRange.end}
                            {" ("}
                            {
                              // Calculate hours between start and end
                              (() => {
                                const start = moment(
                                  tempTimeRange.start,
                                  "HH:mm"
                                );
                                const end = moment(tempTimeRange.end, "HH:mm");
                                const duration = moment.duration(
                                  end.diff(start)
                                );
                                const hours = duration.asHours();
                                return `${hours} ${t("ore") || "ore"}`;
                              })()
                            }
                            {")"}
                          </p>
                          <Button primary onClick={handleTimeRangeConfirm}>
                            {t("conferma_orario") || "Conferma orario"}
                          </Button>
                        </Message>
                      </div>
                    )}
                </>
              ) : (
                <Message warning>
                  {t("nessun_orario_disponibile") ||
                    "Nessun orario disponibile per questa data"}
                </Message>
              )}
            </Grid.Column>
          </Grid.Row>
        )}

        {selectedTimeRange &&
          tempTimeRange &&
          tempTimeRange.start != null &&
          tempTimeRange.end != null && (
            <Grid.Row>
              <Grid.Column width={16}>
                {isLoadingAvailableDates ? (
                  <Loader active inline="centered" />
                ) : availableDates.length > 0 ? (
                  <div>
                    <Message success style={{ textAlign: "center" }}>
                      <Message.Header>
                        {t("orario_confermato") || "Orario confermato"}:{" "}
                        {selectedTimeRange.start} - {selectedTimeRange.end}
                        {" ("}
                        {
                          // Calculate hours between start and end
                          (() => {
                            const start = moment(
                              selectedTimeRange.start,
                              "HH:mm"
                            );
                            const end = moment(selectedTimeRange.end, "HH:mm");
                            const duration = moment.duration(end.diff(start));
                            const hours = duration.asHours();
                            return `${hours} ${t("ore") || "ore"}`;
                          })()
                        }
                        {" )"}
                      </Message.Header>
                      <p>
                        {t("seleziona_date") ||
                          "Seleziona le date per la prenotazione:"}
                      </p>
                    </Message>
                    <Header as="h4">
                      {t("date_disponibili") || "Date disponibili"}
                    </Header>
                    <Segment basic style={{ padding: "10px 0" }}>
                      <DayPickerSingleDateController
                        date={null}
                        onDateChange={handleCalendarDateSelect}
                        focused={focused}
                        onFocusChange={({ focused }) => setFocused({ focused })}
                        isOutsideRange={isOutsideRange}
                        isDayHighlighted={(day) =>
                          selectedDates.some((day2) =>
                            calendarUtils.isSameDay(day, day2)
                          )
                        }
                        renderDayContents={(day) => {
                          const isHighlighted = selectedDates.some((day2) =>
                            calendarUtils.isSameDay(day, day2)
                          );
                          return (
                            <div
                              style={{
                                borderRadius: "0%",
                                background: isHighlighted
                                  ? "#2185d0"
                                  : undefined,
                                color: isHighlighted ? "#fff" : undefined,
                                width: "32px",
                                height: "32px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "auto",
                              }}
                            >
                              {day.date()}
                            </div>
                          );
                        }}
                        numberOfMonths={2}
                        hideKeyboardShortcutsPanel={true}
                      />
                    </Segment>

                    {selectedDates.length > 0 && (
                      <Message info>
                        <Message.Header>
                          {t("date_selezionate") || "Date selezionate"}:
                        </Message.Header>
                        <p>
                          {selectedDates
                            .map((date) => date.format("DD/MM/YYYY"))
                            .join(", ")}
                        </p>
                      </Message>
                    )}
                  </div>
                ) : (
                  <Message warning>
                    {t("nessuna_data_disponibile") ||
                      "Nessuna data disponibile per questo orario"}
                  </Message>
                )}
              </Grid.Column>
            </Grid.Row>
          )}
      </Grid>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <Form.Button onClick={onPrevious}>
          {t("indietro") || "Indietro"}
        </Form.Button>
        <Form.Button primary onClick={onNext} disabled={!canProceed}>
          {t("prosegui") || "Prosegui"}
        </Form.Button>
      </div>
    </Segment>
  );
};

export default Step2;
